import api, { apiService } from './api';
import { LibraryDocument, Pagination } from '../types';
import { fetchAndSave } from '../utils/downloadResponse';

/** Policies and SOPs: the same PDF library, each at its own endpoint and upload field. */
export type LibraryKind = 'policies' | 'sops';

const FIELD: Record<LibraryKind, string> = { policies: 'policy', sops: 'sop' };

export const libraryService = (kind: LibraryKind) => ({
  list: async (params: { page: number; limit: number; search?: string }) => {
    const r = await apiService.get<Record<string, unknown> & { pagination: Pagination }>(`/${kind}`, params);
    return { items: (r.data?.[kind] ?? []) as LibraryDocument[], pagination: r.data!.pagination };
  },

  /** A system admin chooses global or one school; a school's own staff always upload for their school. */
  create: async (data: { title: string; file: File; isGlobal?: boolean; schoolId?: string }) => {
    const form = new FormData();
    form.append('title', data.title);
    form.append(FIELD[kind], data.file);
    if (data.isGlobal !== undefined) form.append('isGlobal', String(data.isGlobal));
    if (data.schoolId) form.append('schoolId', data.schoolId);
    return (await apiService.upload<LibraryDocument>(`/${kind}`, form)).data!;
  },

  update: async (id: string, data: { title: string; isGlobal?: boolean }) =>
    (await apiService.put<LibraryDocument>(`/${kind}/${id}`, data)).data!,

  remove: async (id: string) => { await apiService.delete(`/${kind}/${id}`); },

  download: (doc: LibraryDocument) =>
    fetchAndSave(() => api.get(`/${kind}/${doc._id}/download`, { responseType: 'blob' }), doc.fileName || `${doc.title}.pdf`),

  /** The PDF in a new tab, to read without saving. */
  open: async (doc: LibraryDocument) => {
    const r = await api.get(`/${kind}/${doc._id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
});
